import React, { useState, useEffect, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography as GeoComponent,
} from 'react-simple-maps';
import axios from 'axios';
import { apiPath } from "../apiurl.js";
import LoadingSequence from "../assets/Loading/LoadingSequence";
import exportPDF from '../utils/exportPDF.js';
import worldGeoJson from "../assets/Map/world_countries.json";
import { COUNTRY_CODES } from "../assets/Map/COUNTRY_CODES";
import '../assets/css/Geography.css';
import UserGuideGeography from '../utils/UserGuid/UserGuidMapChart.js';

// ─────────────────────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────────────────────

const DATA_SIZE_LIMIT = 150000;

const IDENTITY_COLORS = [
  { min: 95,  color: '#16a34a', label: '> 95%'  },
  { min: 90,  color: '#65a30d', label: '90–95%' },
  { min: 85,  color: '#ca8a04', label: '85–90%' },
  { min: 80,  color: '#ea580c', label: '80–85%' },
  { min: 75,  color: '#dc2626', label: '75–80%' },
  { min: 0,   color: '#9f1239', label: '< 75%'  },
];

const DEFAULT_RUN_IDS = ['SRR30089172', 'SRR30089168', 'ERR3551195'];
const DEFAULT_SEQ_TYPES = ['2A','2B','2C','3C','3D','3UTR','5UTR','VP1','VP2','VP3','VP4'];
const DEFAULT_REF_IDS   = ['AY184219','AY184220','AY184221','BRA-PE','ISR1318574'];
const DEFAULT_VIR_TYPES = ['PV1','PV2','PV3'];

function getColorByIdentity(pident) {
  for (const { min, color } of IDENTITY_COLORS) if (pident > min) return color;
  return IDENTITY_COLORS[IDENTITY_COLORS.length - 1].color;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────────────────────

function Geography() {
  // ── Run ID state ─────────────────────────────────────────────────────────
  const [runIds,        setRunIds]        = useState(DEFAULT_RUN_IDS);
  const [currentRunId,  setCurrentRunId]  = useState('');

  // ── Data state ────────────────────────────────────────────────────────────
  const [apiData,          setApiData]          = useState(null);
  const [mapData,          setMapData]          = useState([]);
  const [filteredMapData,  setFilteredMapData]  = useState([]);
  const [countryData,      setCountryData]      = useState({});
  const [disclaimer,       setDisclaimer]       = useState('');
  const [noData,           setNoData]           = useState(false);
  const [isLoading,        setIsLoading]        = useState(false);
  const [dataSizeExceeded, setDataSizeExceeded] = useState(false);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [percentIdentityRange,  setPercentIdentityRange]  = useState([0, 100]);
  const [sequenceLengthRange,   setSequenceLengthRange]   = useState([0, 1000]);
  const [selectedSequenceTypes, setSelectedSequenceTypes] = useState(DEFAULT_SEQ_TYPES);
  const [selectedReferenceIds,  setSelectedReferenceIds]  = useState(DEFAULT_REF_IDS);
  const [selectedVirusTypes,    setSelectedVirusTypes]    = useState(DEFAULT_VIR_TYPES);

  // ── Summary state ─────────────────────────────────────────────────────────
  const [totalSequences, setTotalSequences] = useState(0);
  const [totalCountries, setTotalCountries] = useState(0);

  // ── Tooltip state ─────────────────────────────────────────────────────────
  const [tooltip, setTooltip] = useState(null);

  // ── Misc ──────────────────────────────────────────────────────────────────
  const [userGuideOpen, setUserGuideOpen] = useState(false);
  const handleOpenUserGuide = () => setUserGuideOpen(true);
  const handleCloseUserGuide = () => setUserGuideOpen(false);

  // ── Sync filter options from API response ─────────────────────────────────
  useEffect(() => {
    if (!apiData?.stats) return;
    if (apiData.stats.sequenceTypes) {
      const opts = Object.keys(apiData.stats.sequenceTypes);
      if (opts.length) setSelectedSequenceTypes(opts);
    }
    if (apiData.stats.referenceIds) {
      const opts = Object.keys(apiData.stats.referenceIds).filter(id => id !== 'Unknown');
      if (opts.length) setSelectedReferenceIds(opts);
    }
    if (apiData.stats.virusTypes) {
      const opts = Object.keys(apiData.stats.virusTypes).filter(t => t !== 'Unknown');
      if (opts.length) setSelectedVirusTypes(opts);
    }
  }, [apiData]);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (runIds.length > 0) searchApi();
  }, []); // eslint-disable-line

  // ── Apply filters on change ───────────────────────────────────────────────
  useEffect(() => {
    applyFilters();
  }, [mapData, percentIdentityRange, sequenceLengthRange, selectedSequenceTypes, selectedReferenceIds, selectedVirusTypes]); // eslint-disable-line

  // ─────────────────────────────────────────────────────────────────────────
  //  Handlers
  // ─────────────────────────────────────────────────────────────────────────

  const addRunId = () => {
    if (currentRunId && !runIds.includes(currentRunId) && runIds.length < 5) {
      setRunIds([...runIds, currentRunId]);
      setCurrentRunId('');
    }
  };

  const removeRunId = (id) => setRunIds(runIds.filter(r => r !== id));

  const handleKeyDown = (e) => { if (e.key === 'Enter') addRunId(); };

  const resetFilters = () => {
    setPercentIdentityRange([0, 100]);
    setSequenceLengthRange([0, 1000]);
    setSelectedSequenceTypes(DEFAULT_SEQ_TYPES);
    setSelectedReferenceIds(DEFAULT_REF_IDS);
    setSelectedVirusTypes(DEFAULT_VIR_TYPES);
  };

  const clearAll = () => {
    setRunIds(DEFAULT_RUN_IDS);
    setCurrentRunId('');
    resetFilters();
    setApiData(null);
    setMapData([]);
    setFilteredMapData([]);
    setCountryData({});
    setDisclaimer('');
    setNoData(false);
    setIsLoading(false);
    setDataSizeExceeded(false);
    searchApi();
  };

  const handleCheckboxChange = (setter, value, current) => {
    setter(current.includes(value) ? current.filter(v => v !== value) : [...current, value]);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  Data
  // ─────────────────────────────────────────────────────────────────────────

  const applyFilters = () => {
    if (!mapData.length) {
      setFilteredMapData([]); setCountryData({});
      setTotalSequences(0); setTotalCountries(0);
      return;
    }
    const filtered = mapData.filter(item => {
      if (item.pident < percentIdentityRange[0] || item.pident > percentIdentityRange[1]) return false;
      if (item.len    < sequenceLengthRange[0]  || item.len    > sequenceLengthRange[1])  return false;
      if (!selectedSequenceTypes.includes(item.sequenceType)) return false;
      if (!selectedReferenceIds.includes(item.referenceId) && item.referenceId !== 'Unknown') return false;
      if (!selectedVirusTypes.includes(item.virusType)     && item.virusType   !== 'Unknown') return false;
      return true;
    });
    setFilteredMapData(filtered);

    const countries = {};
    filtered.forEach(item => {
      if (!countries[item.countryCode]) {
        countries[item.countryCode] = { totalPident: 0, count: 0, sequences: [] };
      }
      countries[item.countryCode].totalPident += item.pident;
      countries[item.countryCode].count += 1;
      countries[item.countryCode].sequences.push(item);
    });
    Object.keys(countries).forEach(code => {
      const c = countries[code];
      c.avgPident = c.totalPident / c.count;
      c.color = getColorByIdentity(c.avgPident);
      c.name  = COUNTRY_CODES[code]?.name || code;
    });
    setCountryData(countries);
    setTotalSequences(filtered.length);
    setTotalCountries(Object.keys(countries).length);
  };

  const searchApi = async () => {
    setIsLoading(true); setNoData(false); setDataSizeExceeded(false);
    if (!runIds.length) { setIsLoading(false); setNoData(true); return; }

    try {
      let allData = { sequences: [], stats: { totalSequences: 0, totalCountries: 0 } };
      let totalRows = 0; let foundData = false;

      for (const runId of runIds) {
        try {
          const res = await axios.get(`${apiPath}/api/Geographyreadhit/${runId}`);
          if (res.data?.sequences?.length) {
            foundData = true;
            totalRows += res.data.sequences.length;
            if (totalRows > DATA_SIZE_LIMIT) { setDataSizeExceeded(true); break; }
            allData.sequences = [...allData.sequences, ...res.data.sequences];
            if (res.data.stats) {
              ['sequenceTypes','virusTypes','referenceIds','countryCodes'].forEach(key => {
                if (res.data.stats[key]) {
                  allData.stats[key] = allData.stats[key] || {};
                  Object.entries(res.data.stats[key]).forEach(([k, v]) => {
                    allData.stats[key][k] = (allData.stats[key][k] || 0) + v;
                  });
                }
              });
            }
          }
        } catch (e) {
          console.error(`Error fetching ${runId}:`, e);
        }
      }

      setIsLoading(false);
      if (foundData) {
        allData.stats.totalSequences = allData.sequences.length;
        const uniqueCountries = [...new Set(allData.sequences.filter(s => s.countryCode).map(s => s.countryCode))];
        allData.stats.totalCountries = uniqueCountries.length;
        setApiData(allData);
        processMapData(allData);
        setTotalSequences(allData.stats.totalSequences || 0);
        setTotalCountries(allData.stats.totalCountries || 0);
        setDisclaimer(
          dataSizeExceeded
            ? `Data size limit of ${DATA_SIZE_LIMIT.toLocaleString()} rows exceeded. Some data may not be shown.`
            : 'Geographical data is extracted from sequence IDs'
        );
      } else {
        setNoData(true);
        setMapData([]); setFilteredMapData([]); setCountryData({});
        setTotalSequences(0); setTotalCountries(0);
      }
    } catch (e) {
      console.error('Geography search error:', e);
      setIsLoading(false); setNoData(true);
    }
  };

  const processMapData = (data) => {
    if (!data?.sequences?.length) { setMapData([]); return; }
    const processed = data.sequences.map(seq => {
      if (seq.countryCode && COUNTRY_CODES[seq.countryCode]) {
        return {
          ...seq,
          countryName: COUNTRY_CODES[seq.countryCode].name,
          coordinates: COUNTRY_CODES[seq.countryCode].coordinates,
          color: getColorByIdentity(seq.pident),
        };
      }
      return null;
    }).filter(Boolean);
    setMapData(processed);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  Checkbox option lists
  // ─────────────────────────────────────────────────────────────────────────

  const seqTypeOpts = apiData?.stats?.sequenceTypes
    ? Object.keys(apiData.stats.sequenceTypes)
    : DEFAULT_SEQ_TYPES;

  const refIdOpts = apiData?.stats?.referenceIds
    ? Object.keys(apiData.stats.referenceIds).filter(id => id !== 'Unknown')
    : DEFAULT_REF_IDS;

  const virTypeOpts = apiData?.stats?.virusTypes
    ? Object.keys(apiData.stats.virusTypes).filter(t => t !== 'Unknown')
    : DEFAULT_VIR_TYPES;

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="geo-root">

      {/* ── TOP BAR ── */}
      <div className="geo-topbar">
        <div className="geo-topbar-left">
          <div className="geo-topbar-title">Geographical Distribution</div>
          <div className="geo-topbar-sub">Pathogen origin mapping from BLAST sequence identifiers</div>
        </div>
        <div className="geo-topbar-right">
          <div className="geo-stat-chip">
            <span className="geo-stat-chip-label">Sequences</span>
            <span className="geo-stat-chip-val">{totalSequences.toLocaleString()}</span>
          </div>
          <div className="geo-stat-chip">
            <span className="geo-stat-chip-label">Countries</span>
            <span className="geo-stat-chip-val">{totalCountries}</span>
          </div>
          <div className="geo-topbar-sep" />
          <button className="geo-btn geo-btn-ghost geo-btn-sm"
            onClick={() => exportPDF(filteredMapData, countryData)}>
            ↓ Export
          </button>
          <button className="geo-btn geo-btn-ghost geo-btn-sm"
            onClick={() => setUserGuideOpen(true)}>
            ? Guide
          </button>
          <button className="geo-btn geo-btn-ghost geo-btn-sm" onClick={clearAll}>
            ↺ Reset All
          </button>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="geo-body">

        {/* ── LEFT FILTER PANEL ── */}
        <div className="geo-panel">
          <div className="geo-panel-header">
            <span className="geo-panel-header-title">Data Filters</span>
          </div>

          <div className="geo-panel-body">

            {/* Run ID Search */}
            <div className="geo-section">
              <div className="geo-section-label">Run ID Search</div>
              <div className="geo-run-input-row">
                <input
                  className="geo-input"
                  type="text"
                  placeholder="e.g., SRR9686066"
                  value={currentRunId}
                  onChange={e => setCurrentRunId(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className="geo-btn geo-btn-primary geo-btn-sm"
                  onClick={addRunId}
                  disabled={runIds.length >= 5 || !currentRunId}
                >
                  Add
                </button>
              </div>
              <div className="geo-run-meta">Selected ({runIds.length}/5)</div>
              {runIds.length > 0 && (
                <div className="geo-chips">
                  {runIds.map(id => (
                    <span key={id} className="geo-chip">
                      {id}
                      <button className="geo-chip-x" onClick={() => removeRunId(id)}>✕</button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button
                  className="geo-btn geo-btn-primary"
                  onClick={searchApi}
                  disabled={runIds.length === 0 || isLoading}
                  style={{ flex: 1 }}
                >
                  {isLoading ? 'Searching…' : '⌕ Search'}
                </button>
              </div>
              {dataSizeExceeded && (
                <div className="geo-alert geo-alert-warn">
                  Data limit of {DATA_SIZE_LIMIT.toLocaleString()} rows exceeded.
                </div>
              )}
            </div>

            <div className="geo-divider" />

            {/* Percent Identity */}
            <div className="geo-section">
              <div className="geo-section-label">
                Percent Identity ({percentIdentityRange[0]}% – {percentIdentityRange[1]}%)
              </div>
              <div className="geo-range-group">
                <input type="range" className="geo-range" min={0} max={100} step={0.5}
                  value={percentIdentityRange[0]}
                  onChange={e => setPercentIdentityRange([+e.target.value, percentIdentityRange[1]])} />
                <input type="range" className="geo-range" min={0} max={100} step={0.5}
                  value={percentIdentityRange[1]}
                  onChange={e => setPercentIdentityRange([percentIdentityRange[0], +e.target.value])} />
              </div>
              <div className="geo-range-labels">
                <span>0%</span><span>100%</span>
              </div>
            </div>

            <div className="geo-divider" />

            {/* Sequence Length */}
            <div className="geo-section">
              <div className="geo-section-label">
                Sequence Length ({sequenceLengthRange[0]} – {sequenceLengthRange[1]} bp)
              </div>
              <div className="geo-range-group">
                <input type="range" className="geo-range" min={0} max={1000} step={10}
                  value={sequenceLengthRange[0]}
                  onChange={e => setSequenceLengthRange([+e.target.value, sequenceLengthRange[1]])} />
                <input type="range" className="geo-range" min={0} max={1000} step={10}
                  value={sequenceLengthRange[1]}
                  onChange={e => setSequenceLengthRange([sequenceLengthRange[0], +e.target.value])} />
              </div>
              <div className="geo-range-labels">
                <span>0 bp</span><span>1000 bp</span>
              </div>
            </div>

            <div className="geo-divider" />

            {/* Sequence Type */}
            <div className="geo-section">
              <div className="geo-section-label">Sequence Type</div>
              <div className="geo-check-grid">
                {seqTypeOpts.map(type => (
                  <label key={type} className="geo-check-row">
                    <input type="checkbox"
                      checked={selectedSequenceTypes.includes(type)}
                      onChange={() => handleCheckboxChange(setSelectedSequenceTypes, type, selectedSequenceTypes)} />
                    {type} ({apiData?.stats?.sequenceTypes?.[type] ?? 0})
                  </label>
                ))}
              </div>
            </div>

            <div className="geo-divider" />

            {/* Reference ID */}
            <div className="geo-section">
              <div className="geo-section-label">Reference ID</div>
              <div className="geo-check-grid">
                {refIdOpts.map(id => (
                  <label key={id} className="geo-check-row">
                    <input type="checkbox"
                      checked={selectedReferenceIds.includes(id)}
                      onChange={() => handleCheckboxChange(setSelectedReferenceIds, id, selectedReferenceIds)} />
                    {id} ({apiData?.stats?.referenceIds?.[id] ?? 0})
                  </label>
                ))}
              </div>
            </div>

            <div className="geo-divider" />

            {/* Virus Type */}
            <div className="geo-section">
              <div className="geo-section-label">Virus Type</div>
              <div className="geo-check-grid">
                {virTypeOpts.map(type => (
                  <label key={type} className="geo-check-row">
                    <input type="checkbox"
                      checked={selectedVirusTypes.includes(type)}
                      onChange={() => handleCheckboxChange(setSelectedVirusTypes, type, selectedVirusTypes)} />
                    {type} ({apiData?.stats?.virusTypes?.[type] ?? 0})
                  </label>
                ))}
              </div>
            </div>

            <div className="geo-divider" />

            {/* Color Legend */}
            <div className="geo-section">
              <div className="geo-section-label">Avg. Identity Color Scale</div>
              <div className="geo-legend-grid">
                {IDENTITY_COLORS.map(({ color, label }) => (
                  <div key={label} className="geo-legend-item">
                    <span className="geo-legend-swatch" style={{ background: color }} />
                    {label}
                  </div>
                ))}
              </div>
            </div>

            <div className="geo-divider" />

            {/* Summary */}
            <div className="geo-section">
              <div className="geo-section-label">Data Summary</div>
              <div className="geo-summary-grid">
                <div className="geo-summary-card">
                  <div className="geo-summary-label">Sequences</div>
                  <div className="geo-summary-val">{totalSequences.toLocaleString()}</div>
                </div>
                <div className="geo-summary-card">
                  <div className="geo-summary-label">Countries</div>
                  <div className="geo-summary-val">{totalCountries}</div>
                </div>
              </div>
            </div>

          </div>{/* /geo-panel-body */}
        </div>{/* /geo-panel */}

        {/* ── MAP AREA ── */}
        <div className="geo-map-area">
          <div className="geo-map-header">
            <div className="geo-map-header-left">
              <span className="geo-map-title">Geographical Distribution</span>
              <span className="geo-map-badge">{totalCountries} Countries</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isLoading && <div className="geo-spinner" />}
              <button className="geo-btn geo-btn-ghost geo-btn-sm" onClick={clearAll}>
                ↺ Clear All
              </button>
            </div>
          </div>

          <div className="geo-map-canvas">
            {isLoading ? (
              <div className="geo-empty">
                <LoadingSequence message="Loading geographic data" size="medium" />
              </div>
            ) : noData || Object.keys(countryData).length === 0 ? (
              <div className="geo-empty">
                <div className="geo-empty-icon">◎</div>
                <span>{noData ? 'No data available for the selected runs.' : 'No Map Data Available'}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  Enter run IDs and click Search to load geographic data.
                </span>
              </div>
            ) : (
              <>
                <ComposableMap
                  projection="geoMercator"
                  projectionConfig={{ scale: 150 }}
                  style={{ width: '100%', height: '100%' }}
                >
                  <Geographies geography={worldGeoJson}>
                    {({ geographies }) =>
                      geographies.map(geo => {
                        const iso     = geo.id;
                        const country = countryData[iso];
                        return (
                          <GeoComponent
                            key={geo.rsmKey}
                            geography={geo}
                            fill={country ? country.color : '#d1d9e4'}
                            stroke="#ffffff"
                            strokeWidth={0.4}
                            style={{
                              default:  { outline: 'none' },
                              hover:    { outline: 'none', fill: country ? country.color : '#c4cfe0', opacity: country ? 0.82 : 1, cursor: country ? 'pointer' : 'default' },
                              pressed:  { outline: 'none' },
                            }}
                            onMouseEnter={() => country && setTooltip(country)}
                            onMouseLeave={() => setTooltip(null)}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ComposableMap>

                {/* Tooltip */}
                {tooltip && (
                  <div className="geo-map-tooltip">
                    <div className="geo-tooltip-title">
                      <span className="geo-tooltip-dot" style={{ background: tooltip.color }} />
                      {tooltip.name}
                    </div>
                    <div className="geo-tooltip-row">
                      <span>Avg. Identity</span>
                      <span className="geo-tooltip-val">{tooltip.avgPident.toFixed(2)}%</span>
                    </div>
                    <div className="geo-tooltip-row">
                      <span>Sequences</span>
                      <span className="geo-tooltip-val">{tooltip.count.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Disclaimer */}
          {disclaimer && (
            <div className="geo-disclaimer">{disclaimer}</div>
          )}
        </div>
      </div>

      {/* User Guide Modal */}
      {/* {userGuideOpen && <UserGuideGeography open={userGuideOpen} onClose={handleCloseUserGuide} />} */}
    </div>
  );
}

export default Geography;
